import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion, useScroll } from 'framer-motion';
import {
  AlertTriangle,
  ArrowLeft,
  BadgeCheck,
  Bookmark,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Heart,
  MoreHorizontal,
  X,
} from 'lucide-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Badge } from '@/components/ui';
import { CommentsSection } from '@/features/content/components/CommentsSection';
import { ShareMenu } from '@/features/content/components/ShareMenu';
import { useContent } from '@/features/content/hooks/useContent';
import { calculateReadingTime, getThemeLabel } from '@/features/feed/utils';
import { useAuth } from '@/providers';

interface NavigationState {
  origin?: 'feed' | 'library' | 'saved-page';
  libraryTab?: 'saved' | 'liked';
}

// --- Sub-components ---

function ReadingProgressBar() {
  const { scrollYProgress } = useScroll();
  return (
    <motion.div
      className="fixed top-0 left-0 right-0 h-1 bg-[#E94E1B] origin-left z-50"
      style={{ scaleX: scrollYProgress }}
    />
  );
}

function ActionButton({
  icon: Icon,
  label,
  active = false,
  activeColor = '',
  onClick,
}: {
  icon: React.ElementType;
  label: string;
  active?: boolean;
  activeColor?: string;
  onClick: () => void;
}) {
  return (
    <div className="flex flex-col items-center gap-1 group">
      <button
        type="button"
        onClick={onClick}
        className={`w-11 h-11 rounded-lg border flex items-center justify-center transition-all duration-200
          ${active ? `border-[#E94E1B] ${activeColor}` : 'border-[#CAC7C7] text-[#888185] hover:text-[#2E2A23] hover:border-[#888185] bg-transparent'}`}
        aria-label={label}
      >
        <Icon
          className={`w-5 h-5 transition-transform duration-200 ${active ? 'scale-110' : 'group-active:scale-95'}`}
        />
      </button>
    </div>
  );
}

function NavButton({
  direction,
  disabled,
  onClick,
}: {
  direction: 'prev' | 'next';
  disabled?: boolean;
  onClick?: () => void;
}) {
  const Icon = direction === 'prev' ? ChevronLeft : ChevronRight;
  const label = direction === 'prev' ? 'Anterior' : 'Próximo';
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-2 rounded-md transition-colors
        ${disabled ? 'text-[#CAC7C7] cursor-not-allowed' : 'text-[#888185] hover:bg-[#F4F2F2] hover:text-[#2E2A23]'}`}
    >
      {direction === 'prev' && <Icon className="w-4 h-4" />}
      <span className="text-sm font-medium hidden sm:inline">{label}</span>
      {direction === 'next' && <Icon className="w-4 h-4" />}
    </button>
  );
}

function LoadingSkeleton() {
  return (
    <div className="min-h-screen w-full bg-[#F4F2F2] animate-pulse">
      <div className="h-14 w-full border-b border-[#CAC7C7] bg-[#F4F2F2]" />
      <div className="max-w-[680px] mx-auto px-6 pt-16">
        <div className="h-4 w-32 bg-[#CAC7C7] rounded mx-auto mb-8" />
        <div className="h-8 w-3/4 bg-[#CAC7C7] rounded mx-auto mb-4" />
        <div className="h-8 w-1/2 bg-[#CAC7C7] rounded mx-auto mb-10" />
        <div className="flex justify-center gap-4 mb-12">
          <div className="h-4 w-24 bg-[#CAC7C7] rounded" />
          <div className="h-4 w-24 bg-[#CAC7C7] rounded" />
          <div className="h-4 w-24 bg-[#CAC7C7] rounded" />
        </div>
        <div className="space-y-4">
          <div className="h-4 w-full bg-[#CAC7C7] rounded" />
          <div className="h-4 w-full bg-[#CAC7C7] rounded" />
          <div className="h-4 w-full bg-[#CAC7C7] rounded" />
          <div className="h-4 w-5/6 bg-[#CAC7C7] rounded" />
        </div>
        <div className="mt-12 space-y-4">
          <div className="h-4 w-full bg-[#CAC7C7] rounded" />
          <div className="h-4 w-full bg-[#CAC7C7] rounded" />
          <div className="h-4 w-4/6 bg-[#CAC7C7] rounded" />
        </div>
      </div>
    </div>
  );
}

function ErrorState() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen w-full bg-[#F4F2F2] flex flex-col items-center justify-center p-6 text-center">
      <div className="w-16 h-16 bg-[#F4F2F2] rounded-full flex items-center justify-center mb-6">
        <AlertTriangle className="w-8 h-8 text-[#E94E1B]" />
      </div>
      <h2 className="font-serif text-2xl text-[#2E2A23] mb-2">Não foi possível carregar</h2>
      <p className="text-[#888185] max-w-sm mb-8">
        Houve um problema ao processar este conteúdo. Por favor, tente novamente mais tarde.
      </p>
      <button
        type="button"
        onClick={() => navigate('/feed')}
        className="px-6 py-2 bg-[#2E2A23] text-[#F4F2F2] rounded-lg text-sm font-medium hover:bg-[#403b36] transition-colors"
      >
        Voltar ao feed
      </button>
    </div>
  );
}

// --- Main Component ---

export function ContentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const navigationState = (location.state as NavigationState) || {};
  const {
    content,
    userState,
    isLoading,
    isError,
    toggleLike,
    toggleSave,
    dismiss,
    feedback,
    muteAuthor,
    muteSource,
    hasPrevious,
    hasNext,
    goToPrevious,
    goToNext,
    shareToLinkedIn,
    shareToTwitter,
    shareToWhatsApp,
    copyShareLink,
  } = useContent(id || '');

  const handleBack = useCallback(() => {
    // Invalidate feed to refresh with updated view tracking
    queryClient.invalidateQueries({ queryKey: ['feed', user?.id] });

    // Navigate back to the appropriate view based on origin
    if (navigationState.origin === 'saved-page') {
      // Go back to standalone SavedPage with the correct tab
      navigate('/saved', {
        state: { tab: navigationState.libraryTab || 'saved' },
      });
    } else if (navigationState.origin === 'library') {
      // Go back to FeedPage's library view with the correct tab
      navigate('/feed', {
        state: { view: 'library', tab: navigationState.libraryTab || 'saved' },
      });
    } else {
      // Default: go back to feed
      navigate('/feed');
    }
  }, [navigate, navigationState, queryClient, user?.id]);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isShareMenuOpen, setIsShareMenuOpen] = useState(false);
  const [showToast, setShowToast] = useState<{
    visible: boolean;
    message: string;
    icon?: React.ReactNode;
  }>({
    visible: false,
    message: '',
  });

  const triggerToast = (message: string, icon?: React.ReactNode) => {
    setShowToast({ visible: true, message, icon });
    setTimeout(() => setShowToast({ visible: false, message: '' }), 3000);
  };

  const handleLike = () => {
    toggleLike();
  };

  const handleSave = () => {
    toggleSave();
    if (!userState.isSaved) {
      triggerToast('Salvo na sua biblioteca', <BadgeCheck className="w-4 h-4 text-[#E94E1B]" />);
    }
  };

  const handleDismiss = () => {
    triggerToast('Item removido');
    dismiss();
  };

  const handleFeedback = (type: 'less' | 'mute_author' | 'mute_source') => {
    setIsMenuOpen(false);
    switch (type) {
      case 'less':
        feedback();
        triggerToast('Preferência registrada');
        break;
      case 'mute_author':
        muteAuthor();
        break;
      case 'mute_source':
        muteSource();
        break;
    }
  };

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (isError || !content) {
    return <ErrorState />;
  }

  // Format date
  const formattedDate = new Date(content.publishedAt).toLocaleDateString('pt-BR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="min-h-screen w-full bg-[#F4F2F2] text-[#2E2A23] font-sans selection:bg-[#FDEAE3] selection:text-[#E94E1B]">
      <ReadingProgressBar />

      {/* Header */}
      <header className="sticky top-0 z-40 w-full transition-all duration-200 bg-[#F4F2F2]/90 backdrop-blur-sm border-b border-[#CAC7C7]">
        <div className="max-w-[960px] mx-auto px-6 h-14 flex items-center justify-between">
          <button
            type="button"
            className="group flex items-center gap-2 text-[#888185] hover:text-[#2E2A23] transition-colors"
            onClick={handleBack}
          >
            <ArrowLeft className="w-5 h-5 transition-transform group-hover:-translate-x-1" />
            <span className="text-sm font-medium">Voltar</span>
          </button>

          {content.sourceUrl && (
            <a
              href={content.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-1.5 text-[#E94E1B] hover:underline"
            >
              <span className="text-sm">Fonte original</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[960px] mx-auto px-6 pt-12 pb-32 md:px-12 md:pt-16 min-h-[calc(100vh-56px)] flex flex-col">
        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="flex-1"
        >
          {/* Article Header */}
          <div className="text-center mb-12">
            <div className="inline-block mb-6">
              <Badge variant="theme">{getThemeLabel(content.theme, t)}</Badge>
            </div>

            <h1 className="font-serif text-[26px] md:text-[32px] leading-[1.25] font-semibold text-[#2E2A23] mb-5">
              {content.title}
            </h1>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-0 text-sm text-[#888185]">
              <div className="flex items-center gap-1.5">
                <span>Por {content.author}</span>
                <BadgeCheck className="w-3.5 h-3.5 text-[#E94E1B]" />
              </div>
              <span className="hidden sm:inline mx-2">·</span>
              <span>{formattedDate}</span>
              <span className="hidden sm:inline mx-2">·</span>
              <span>
                {t('content.readingTime', {
                  minutes: content.readingTimeMinutes || calculateReadingTime(content.body),
                })}
              </span>
            </div>

            <div className="w-20 h-px bg-[#CAC7C7] mx-auto mt-8" />
          </div>

          {/* Article Body */}
          <div
            className="prose prose-lg max-w-none prose-headings:font-serif prose-headings:text-[#2E2A23] prose-p:font-serif prose-p:text-[18px] prose-p:leading-[1.8] prose-p:text-[#2E2A23] prose-a:text-[#E94E1B] prose-a:no-underline hover:prose-a:underline prose-blockquote:border-l-4 prose-blockquote:border-[#CAC7C7] prose-blockquote:pl-6 prose-blockquote:italic prose-blockquote:text-[#888185] prose-li:font-serif prose-li:text-[18px] prose-li:text-[#2E2A23]"
            dangerouslySetInnerHTML={{ __html: content.body || `<p>${content.summary}</p>` }}
          />
        </motion.article>

        {/* Comments Section - positioned outside article for consistent end-of-page placement */}
        <CommentsSection contentId={id || ''} />
      </main>

      {/* Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-[#F4F2F2] border-t border-[#CAC7C7] z-40 pb-[env(safe-area-inset-bottom)]">
        <div className="max-w-[960px] mx-auto px-6 h-[72px] flex items-center justify-between">
          {/* Action Buttons Group */}
          <div className="flex items-center gap-3">
            <ActionButton
              icon={Heart}
              label="Útil"
              active={userState.isLiked}
              activeColor="text-[#E94E1B] fill-[#E94E1B]"
              onClick={handleLike}
            />
            <ActionButton
              icon={Bookmark}
              label="Salvar"
              active={userState.isSaved}
              activeColor="text-[#E94E1B] fill-[#E94E1B] bg-[#FDEAE3]"
              onClick={handleSave}
            />
            <ActionButton icon={X} label="Pular" onClick={handleDismiss} />
            <ShareMenu
              isOpen={isShareMenuOpen}
              onToggle={() => {
                setIsShareMenuOpen(!isShareMenuOpen);
                setIsMenuOpen(false);
              }}
              onShareLinkedIn={shareToLinkedIn}
              onShareTwitter={shareToTwitter}
              onShareWhatsApp={shareToWhatsApp}
              onCopyLink={copyShareLink}
            />
            <div className="relative">
              <ActionButton
                icon={MoreHorizontal}
                label="Mais"
                active={isMenuOpen}
                onClick={() => {
                  setIsMenuOpen(!isMenuOpen);
                  setIsShareMenuOpen(false);
                }}
              />

              <AnimatePresence>
                {isMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    className="absolute bottom-full left-0 mb-3 w-48 bg-white rounded-lg shadow-xl border border-[#CAC7C7] overflow-hidden origin-bottom-left"
                  >
                    <div className="py-1">
                      <button
                        type="button"
                        className="w-full text-left px-4 py-3 text-sm text-[#2E2A23] hover:bg-[#F4F2F2]"
                        onClick={() => handleFeedback('mute_author')}
                      >
                        Silenciar autor
                      </button>
                      <button
                        type="button"
                        className="w-full text-left px-4 py-3 text-sm text-[#2E2A23] hover:bg-[#F4F2F2]"
                        onClick={() => handleFeedback('mute_source')}
                      >
                        Silenciar fonte
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Navigation Group */}
          <div className="flex items-center gap-2">
            <NavButton direction="prev" disabled={!hasPrevious} onClick={goToPrevious} />
            <NavButton direction="next" disabled={!hasNext} onClick={goToNext} />
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      <AnimatePresence>
        {showToast.visible && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-24 left-1/2 -translate-x-1/2 bg-[#2E2A23] text-[#F4F2F2] px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[200px] justify-center"
          >
            {showToast.icon}
            <span className="text-sm font-medium">{showToast.message}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
